import { useMutation, useQuery, useQueryClient } from "react-query";

import { BaseSubscriptionDto } from "doxecure-commons/doxecureClientTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { 
    buyCreditsPack,
    cancelDowngradingRenewalToStandardSubscription,
    cancelReducingUsersRequest,
    cancelTerminationSubscriptionRequest,
    getActiveSubscription,
    purchaseCollaborators,
    requestDowngradeRenewalToStandardSubscription,
    requestRemoveCollaborators,
    requestTerminatingSubscription,
    startSubscriptionNow,
    upgradeSubscriptionToUnlimited
} from "@services/corporations.service";

import { OrdersID, SubscriptionID } from "./hooksConstants";
import { useAuthContext } from "./useAuthContext";

export const useSubscription = (isPendingPayment?: boolean) => {
    const { connectedUser, isAuthenticated } = useAuthContext();
    const queryClient = useQueryClient();

    const corporationApid = connectedUser?.corporations?.first()?.apid;

    const query = useQuery<Nullable<BaseSubscriptionDto>>({
        queryKey: [SubscriptionID, corporationApid],
        queryFn: () => getActiveSubscription(corporationApid),
        enabled: isAuthenticated && $ok(corporationApid),
        staleTime: 5 * 60 * 1000,
        refetchInterval: !!isPendingPayment ? 2000 : false,
        refetchOnWindowFocus: true,
    });

    const invalidate = () => {
        queryClient.invalidateQueries([SubscriptionID, corporationApid]);
        queryClient.invalidateQueries([OrdersID, corporationApid]);
    };

    const buyCollaborators = useMutation({
        mutationFn: (count: number) => purchaseCollaborators(corporationApid, count),
        onSuccess: invalidate
    });

    const buyExtraPack = useMutation({
        mutationFn: () => buyCreditsPack(corporationApid),
        onSuccess: invalidate
    });

    const cancelDowngradingRequest = useMutation({
        mutationFn: () => cancelDowngradingRenewalToStandardSubscription(corporationApid),
        onSuccess: invalidate
    });

    const cancelReducingRequest = useMutation({
        mutationFn: () => cancelReducingUsersRequest(corporationApid),
        onSuccess: invalidate
    });

    const cancelTerminationRequest = useMutation({
        mutationFn: () => cancelTerminationSubscriptionRequest(corporationApid),
        onSuccess: invalidate
    });

    const requestCancelSubscription = useMutation({
        mutationFn: () => requestTerminatingSubscription(corporationApid),
        onSuccess: invalidate,
    });

    const requestDowngradRenewalToStandardPlan = useMutation({
        mutationFn: () => requestDowngradeRenewalToStandardSubscription(corporationApid),
        onSuccess: invalidate,
    });

    const requestReduceCollaborators = useMutation({
        mutationFn: (count: number) => requestRemoveCollaborators(corporationApid, count),
        onSuccess: invalidate,
    })

    const startSubscriptionImmediately = useMutation({
        mutationFn: (unlimited: boolean) => startSubscriptionNow(corporationApid, unlimited),
        onSuccess: invalidate,
    });

    const upgradeToUnlimited = useMutation({
        mutationFn: () => upgradeSubscriptionToUnlimited(corporationApid),
        onSuccess: invalidate,
    });

    return {
        ...query,
        buyCollaborators,
        buyExtraPack,
        cancelDowngradingRequest,
        cancelReducingRequest,
        cancelTerminationRequest,
        refreshSubscription: invalidate,
        requestCancelSubscription,
        requestDowngradRenewalToStandardPlan,
        requestReduceCollaborators,
        startSubscriptionImmediately,
        upgradeToUnlimited
    }
}